import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Divider, CloseOutlined, Skeleton } from "src/assets/imports/antdComponents";
import { IHighlightedDialog, ITranscriptViewProps, TIMING } from "../../models/interfaces";
import "./style.css";

const getDialogHighlightRanges = (dialogId: number, highlightedDialogs: IHighlightedDialog[]): Array<{ start: number; end: number }> | null => {
	if (!highlightedDialogs || highlightedDialogs.length === 0) {
		return null;
	}

	const dialogData = highlightedDialogs.find((d) => parseInt(d.dialog_no) === dialogId);

	if (!dialogData || !dialogData.dialog_highlights) {
		return null;
	}

	return dialogData.dialog_highlights.map((h) => ({
		start: parseInt(h.start_index),
		end: parseInt(h.end_index),
	}));
};

const highlightTextRanges = (text: string, ranges: Array<{ start: number; end: number }>): React.ReactNode => {
	if (!ranges || ranges.length === 0 || !text) {
		return text;
	}

	const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
	const mergedRanges: Array<{ start: number; end: number }> = [];

	sortedRanges.forEach((range) => {
		const validRange = {
			start: Math.max(0, Math.min(range.start, text.length)),
			end: Math.max(0, Math.min(range.end, text.length)),
		};

		if (validRange.start >= validRange.end) return;

		if (mergedRanges.length === 0) {
			mergedRanges.push(validRange);
		} else {
			const lastRange = mergedRanges[mergedRanges.length - 1];
			if (validRange.start <= lastRange.end) {
				lastRange.end = Math.max(lastRange.end, validRange.end);
			} else {
				mergedRanges.push(validRange);
			}
		}
	});

	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	let key = 0;

	mergedRanges.forEach((range) => {
		if (range.start > lastIndex) {
			parts.push(<span key={`text-${key++}`}>{text.slice(lastIndex, range.start)}</span>);
		}

		parts.push(
			<span key={`highlight-${key++}`} className="highlight-block">
				{text.slice(range.start, range.end)}
			</span>,
		);

		lastIndex = range.end;
	});

	if (lastIndex < text.length) {
		parts.push(<span key={`text-${key++}`}>{text.slice(lastIndex)}</span>);
	}

	return <>{parts}</>;
};

const ReflectiveTranscriptView: React.FC<ITranscriptViewProps> = ({
	transcriptName,
	createdBy,
	transcriptItems,
	onClose,
	isLoadingTranscript,
	isExpanded,
	handleExpand,
	module,
	highlightedDialogs,
	firstDialogNo,
}) => {
	const { t } = useTranslation();
	const [currentMentionIndex, setCurrentMentionIndex] = useState(0);
	const [highlightedItems, setHighlightedItems] = useState<any[]>([]);
	const transcriptBodyRef = useRef<HTMLDivElement>(null);
	const isManualNavigationRef = useRef(false);
	const hasInitialScrolledRef = useRef(false);

	const dialogNos = useMemo(() => highlightedDialogs?.map((d) => parseInt(d.dialog_no)), [highlightedDialogs]);
	const matchedItems = useMemo(
		() => (Array.isArray(transcriptItems) ? transcriptItems.filter((item) => dialogNos?.includes(item?.dialog_id)) : []),
		[transcriptItems, dialogNos],
	);

	const scrollToItem = (element: HTMLElement) => {
		if (transcriptBodyRef.current) {
			transcriptBodyRef.current.scrollTo({ top: element.offsetTop, behavior: "smooth" });
		}
	};

	useEffect(() => {
		if (!isLoadingTranscript && matchedItems.length > 0) {
			setHighlightedItems(matchedItems);
			setCurrentMentionIndex(0);

			if (!hasInitialScrolledRef.current) {
				const scrollTargetId = firstDialogNo || matchedItems[0]?.dialog_id;

				if (scrollTargetId) {
					const timerId = setTimeout(() => {
						const element = document.getElementById(`transcript-item-${scrollTargetId}`);
						if (element) {
							scrollToItem(element);
							hasInitialScrolledRef.current = true;
						}
					}, TIMING?.SCROLL_DELAY);

					return () => clearTimeout(timerId);
				}
			}
		}
	}, [isLoadingTranscript, matchedItems, firstDialogNo]);

	useEffect(() => {
		return () => {
			hasInitialScrolledRef.current = false;
		};
	}, [highlightedDialogs]);

	const updateCurrentMentionOnScroll = useCallback(() => {
		if (!transcriptBodyRef.current || isManualNavigationRef.current || highlightedItems.length === 0) return;

		const containerRect = transcriptBodyRef.current.getBoundingClientRect();
		const containerTop = containerRect.top;

		let mostVisibleItem = null;
		let maxVisibleArea = 0;

		for (let i = 0; i < highlightedItems.length; i++) {
			const item = highlightedItems[i];
			const element = document.getElementById(`transcript-item-${item.dialog_id}`);

			if (element) {
				const rect = element.getBoundingClientRect();
				const visibleTop = Math.max(rect.top, containerTop);
				const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
				const visibleHeight = Math.max(0, visibleBottom - visibleTop);
				const visibleArea = visibleHeight * rect.width;

				const topBias = 1 - (rect.top - containerTop) / containerRect.height;
				const weightedVisibleArea = visibleArea * (1 + topBias);

				if (visibleArea > 0 && weightedVisibleArea > maxVisibleArea) {
					maxVisibleArea = weightedVisibleArea;
					mostVisibleItem = i;
				}
			}
		}

		if (mostVisibleItem !== null) {
			setCurrentMentionIndex((prevIndex) => {
				// Only update if it's actually different
				return prevIndex !== mostVisibleItem ? mostVisibleItem : prevIndex;
			});
		}
	}, [highlightedItems]);

	useEffect(() => {
		const transcriptBody = transcriptBodyRef.current;
		if (!transcriptBody || highlightedItems.length === 0) return;

		transcriptBody.addEventListener("scroll", updateCurrentMentionOnScroll);
		updateCurrentMentionOnScroll();

		return () => {
			transcriptBody.removeEventListener("scroll", updateCurrentMentionOnScroll);
		};
	}, [highlightedItems, updateCurrentMentionOnScroll]);

	const handlePreviousMention = () => {
		if (currentMentionIndex > 0) {
			const newIndex = currentMentionIndex - 1;
			isManualNavigationRef.current = true;
			setCurrentMentionIndex(newIndex);

			const item = highlightedItems[newIndex];
			if (item) {
				const element = document.getElementById(`transcript-item-${item?.dialog_id}`);
				if (element) {
					scrollToItem(element);
					setTimeout(() => {
						isManualNavigationRef.current = false;
					}, TIMING?.NAVIGATION_DEBOUNCE);
				}
			}
		}
	};

	const handleNextMention = () => {
		if (currentMentionIndex < highlightedItems.length - 1) {
			const newIndex = currentMentionIndex + 1;
			isManualNavigationRef.current = true;
			setCurrentMentionIndex(newIndex);

			const item = highlightedItems[newIndex];
			if (item) {
				const element = document.getElementById(`transcript-item-${item?.dialog_id}`);
				if (element) {
					scrollToItem(element);
					setTimeout(() => {
						isManualNavigationRef.current = false;
					}, 800);
				}
			}
		}
	};

	const renderDialog = useCallback(
		(item: any, index: number) => {
			const dialogId = item?.dialog_id;
			const dialogText = item?.dialog || "";
			const speaker = item?.speaker_type || "";

			const highlightRanges = getDialogHighlightRanges(dialogId, highlightedDialogs);
			const isHighlighted = highlightRanges && highlightRanges.length > 0;
			const dialogContent = isHighlighted ? highlightTextRanges(dialogText, highlightRanges) : dialogText;

			return (
				<React.Fragment key={index}>
					<div id={`transcript-item-${dialogId}`} className="transcript-item">
						<p className="transcript-drawer-text text-base-1 flex gap-3 flex-col">
							<p className="transcript-speaker font-semibold text-[--secondary-color-2]">
								{speaker}
								{module && module === "Insights" && ` (${speaker?.charAt(0)?.toUpperCase()})`}
							</p>

							<p className={`font-normal ${isHighlighted ? "highlight-container" : ""}`}>
								<span className="text-[--neutral-grey] font-normal break-words">{dialogContent}</span>
							</p>
						</p>
					</div>
					<Divider className="my-4" />
				</React.Fragment>
			);
		},
		[highlightedDialogs, module],
	);

	return (
		<div className="transcript-view relative">
			<div className="transcript-header bg-[--drawer-header-bg]">
				<div className="transcript-info">
					<div className="flex items-center justify-start gap-1">
						<div className="text-[--secondary-color] text-base-14 ml-1">{t("References")}</div>
					</div>
				</div>
				<i className="material-symbols-outlined text-[1.5rem] cursor-pointer text-[--secondary-color] absolute right-16 mr-4" onClick={handleExpand}>
					{isExpanded ? "hide" : "expand_content"}
				</i>
				<CloseOutlined onClick={onClose} className="secondary-color p-2 text-base" />
			</div>

			<div className="flex items-center mb-2 mt-4 ml-6">
				<i className="material-symbols-outlined text-2xl text-[--secondary-color] cursor-pointer" onClick={onClose}>
					keyboard_arrow_left
				</i>
				<div className="font-semibold text-base-1 line-clamp-2 truncate overflow-hidden whitespace-nowrap text-ellipsis secondary-color">{transcriptName}</div>
			</div>

			{highlightedItems.length > 1 && (
				<div className="mention-navigation-floating">
					<div className="mention-navigation-label">
						{currentMentionIndex + 1} / {highlightedItems.length}
					</div>
					<Divider type="vertical" className="mention-divider" />
					<button
						onClick={handlePreviousMention}
						disabled={currentMentionIndex === 0}
						className={`mention-nav-button ${currentMentionIndex === 0 ? "mention-nav-disabled" : ""}`}
						aria-label="Previous mention">
						<i className="material-symbols-outlined">keyboard_arrow_up</i>
					</button>
					<button
						onClick={handleNextMention}
						disabled={currentMentionIndex === highlightedItems.length - 1}
						className={`mention-nav-button ${currentMentionIndex === highlightedItems.length - 1 ? "mention-nav-disabled" : ""}`}
						aria-label="Next mention">
						<i className="material-symbols-outlined">keyboard_arrow_down</i>
					</button>
				</div>
			)}

			<div className="transcript-body" ref={transcriptBodyRef}>
				{isLoadingTranscript ? (
					Array.from({ length: 3 }).map((_, idx) => (
						<React.Fragment key={idx}>
							<Skeleton active />
							<Divider className="my-6" />
						</React.Fragment>
					))
				) : Array.isArray(transcriptItems) && transcriptItems.length > 0 ? (
					transcriptItems.map((item, index) => renderDialog(item, index))
				) : (
					<div className="no-transcript text-[x-small]">{t("No transcript available")}</div>
				)}
			</div>
		</div>
	);
};

export default React.memo(ReflectiveTranscriptView);
